// src/components/MapaColecta.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import styles from "../style/Transportadora.module.css";

import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const GEOJSON_COMUNA_URL = "https://marketbusters.pro/Dropdrop/map.geojson";
const GEOJSON_MACROZONA_URL = "https://marketbusters.pro/Dropdrop/map_macrozona_5zonas.geojson";
const JSON_COLECTA_URL = "https://marketbusters.pro/Dropdrop/pedidos_listado_preparando_guia.json";

// ---------- helpers ----------
const normKey = (s) =>
  (s || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/\s+/g, " ").trim();

const cleanUbigeo = (input) => {
  let s = String(input || "");
  s = s.replace(/regi[oó]n\s+metropolitana\s+de\s+santiago,\s*/iu, "").trim();
  const plain = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const fixes = { nunoa: "Ñuñoa", maipu: "Maipú", "san joaquin": "San Joaquín", "estacion central": "Estación Central", conchali: "Conchalí" };
  if (fixes[plain]) return fixes[plain];
  return s.toLowerCase().split(/\s+/).map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");
};

// paleta por cantidad (0 = tenue para no confundir con fondo)
const colorByCount = (n) =>
  n >= 8 ? "#d73027" : n >= 5 ? "#fc8d59" : n >= 2 ? "#fee08b" : n >= 1 ? "#d9ef8b" : "#3a3f4b";

// 🎨 colores fijos por macrozona
const MACRO_COLORS = {
  norte:   "#60a5fa",
  sur:     "#f87171",
  oriente: "#34d399",
  poniente:"#f59e0b",
  rural:   "#a78bfa",
};
const getMacroName = (props = {}) =>
  props.Macrozona || props.MACROZONA || props.macrozona || props.macroZona || "";
const macroColor = (name) => MACRO_COLORS[normKey(name)] || "#9AA3B2";

// point-in-polygon helpers
function pointInRing([x, y], ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}
function pointInPolygon([x, y], geom) {
  if (!geom) return false;
  if (geom.type === "Polygon") {
    const outer = geom.coordinates?.[0] || [];
    return pointInRing([x, y], outer);
  }
  if (geom.type === "MultiPolygon") {
    for (const poly of geom.coordinates || []) {
      const outer = poly?.[0] || [];
      if (pointInRing([x, y], outer)) return true;
    }
  }
  return false;
}

const labelIcon = (text) =>
  L.divIcon({ className: styles.labelIcon, html: `<div>${text}</div>`, iconSize: null });

// 🎛️ estilo de botón seleccionado
const SELECTED_BTN_STYLE = {
  background: "#2b364b",
  border: "1px solid rgba(255,255,255,0.22)",
  boxShadow: "0 0 0 2px rgba(255,255,255,0.10) inset",
};

export default function MapaColecta({ refreshKey }) {
  const [comunasData, setComunasData] = useState(null);
  const [macrosData, setMacrosData] = useState(null);
  const [rows, setRows] = useState([]);

  // filtros: 'todos' | 'preparando' | 'guia' | 'novedad' | 'encamino' | 'recolectado'
  const [filter, setFilter] = useState("todos");
  const [viewMode, setViewMode] = useState("comunas");  // comunas | macrozonas

  const mapRef = useRef(null);
  const geojsonRef = useRef(null);
  const shouldFitRef = useRef(true);

  // cargar geojson una vez
  useEffect(() => {
    let a = false;
    (async () => {
      try {
        const res = await fetch(GEOJSON_COMUNA_URL, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const gj = await res.json();
        if (!a) setComunasData(gj);
      } catch (e) { console.error("GeoJSON comunas:", e); }
    })();
    (async () => {
      try {
        const res = await fetch(GEOJSON_MACROZONA_URL, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const gj = await res.json();
        if (!a) setMacrosData(gj);
      } catch (e) { console.error("GeoJSON macrozona:", e); }
    })();
    return () => { a = true; };
  }, []);

  // cargar pedidos
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const res = await fetch(JSON_COLECTA_URL, { cache: "no-store", mode: "cors" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data?.items) ? data.items : [];
        if (!abort) setRows(list);
      } catch (e) { console.error("Pedidos colecta:", e); }
    })();
    return () => { abort = true; };
  }, [refreshKey]);

  // match del filtro por estado
  const matchState = (s, kind) => {
    const t = String(s || "")
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    switch (kind) {
      case "preparando":  return /\bprepar/.test(t);
      case "guia":        return /\bguia\b/.test(t);                  // guía/guia
      case "novedad":     return /\bnovedad\b/.test(t);
      case "encamino":    return /\ben\s*camino\b|\bcamino\b/.test(t);
      case "recolectado": return /\brecolectad/.test(t);               // recolectado
      default:            return true;
    }
  };

  // 🔢 conteos para botones (TODOS = total filas reales)
  const btnCounts = useMemo(() => {
    let preparando = 0, guia = 0, novedad = 0, encamino = 0, recolectado = 0;
    for (const it of rows) {
      const st = it?.trackindPedido;
      if (matchState(st, "preparando"))  preparando++;
      if (matchState(st, "guia"))        guia++;
      if (matchState(st, "novedad"))     novedad++;
      if (matchState(st, "encamino"))    encamino++;
      if (matchState(st, "recolectado")) recolectado++;
    }
    const todos = rows.length;
    return { todos, preparando, guia, novedad, encamino, recolectado };
  }, [rows]);

  // conteo por COMUNA (aplicando filtro)
  const conteoComuna = useMemo(() => {
    const idx = {};
    for (const it of rows) {
      if (!matchState(it?.trackindPedido, filter)) continue;
      const key = normKey(cleanUbigeo(it?.ubigeoComprador));
      idx[key] = (idx[key] || 0) + 1;
    }
    return idx;
  }, [rows, filter]);

  // índice: comuna -> {feature, name, center}
  const comunasIndex = useMemo(() => {
    if (!comunasData) return {};
    const feats = comunasData.type === "FeatureCollection" ? (comunasData.features || [])
                 : comunasData.type === "Feature" ? [comunasData] : [];
    const out = {};
    for (const f of feats) {
      const name = f?.properties?.Comuna || f?.properties?.COMUNA || f?.properties?.NOM_COM || f?.properties?.name;
      if (!name) continue;
      const key = normKey(name);
      const center = L.geoJSON(f).getBounds().getCenter();
      out[key] = { feature: f, name, center: [center.lat, center.lng] };
    }
    return out;
  }, [comunasData]);

  // mapeo comuna -> macrozona (por centroide)
  const comunaToMacro = useMemo(() => {
    if (!macrosData || !comunasIndex) return {};
    const macros = macrosData.type === "FeatureCollection" ? (macrosData.features || [])
                 : macrosData.type === "Feature" ? [macrosData] : [];
    const out = {};
    for (const [ckey, { feature: cFeat }] of Object.entries(comunasIndex)) {
      const center = L.geoJSON(cFeat).getBounds().getCenter();
      const pt = [center.lng, center.lat];
      let chosen = "";
      for (const mf of macros) {
        const mName = getMacroName(mf?.properties) || "";
        if (!mName) continue;
        if (pointInPolygon(pt, mf?.geometry)) { chosen = mName; break; }
      }
      out[ckey] = chosen || "Rural";
    }
    return out;
  }, [macrosData, comunasIndex]);

  // totales por macrozona y leyenda
  const { macroLegend, macroTotals, macrosConComunasActivas } = useMemo(() => {
    const base = {
      norte:   { label: "Norte",   color: MACRO_COLORS.norte,   items: [] },
      sur:     { label: "Sur",     color: MACRO_COLORS.sur,     items: [] },
      oriente: { label: "Oriente", color: MACRO_COLORS.oriente, items: [] },
      poniente:{ label: "Poniente",color: MACRO_COLORS.poniente,items: [] },
      rural:   { label: "Rural",   color: MACRO_COLORS.rural,   items: [] },
    };
    const totals = { norte: 0, sur: 0, oriente: 0, poniente: 0, rural: 0 };
    const activeSet = new Set();

    for (const [ckey, count] of Object.entries(conteoComuna)) {
      if (count <= 0) continue;
      const macroName = comunaToMacro[ckey] || "Rural";
      const bucket = normKey(macroName);
      const nice = comunasIndex[ckey]?.name || ckey;
      const item = `${nice} (${count})`;
      (base[bucket] ? base[bucket].items : base.rural.items).push(item);
      if (totals[bucket] !== undefined) totals[bucket] += count; else totals.rural += count;
      activeSet.add(bucket);
    }
    Object.values(base).forEach(b => b.items.sort((a,b) => a.localeCompare(b, "es")));
    return { macroLegend: base, macroTotals: totals, macrosConComunasActivas: activeSet };
  }, [conteoComuna, comunaToMacro, comunasIndex]);

  const displayGeoJSON = viewMode === "comunas" ? comunasData : macrosData;

  // fit bounds solo primera carga / cambio de modo
  useEffect(() => {
    const m = mapRef.current;
    const g = geojsonRef.current;
    if (m && g && displayGeoJSON && shouldFitRef.current) {
      const b = g.getBounds?.();
      if (b && b.isValid()) m.fitBounds(b, { padding: [20, 20] });
      shouldFitRef.current = false;
    }
  }, [displayGeoJSON]);

  // labels (oculta nombre de macrozona si hay comunas activas dentro)
  const labels = useMemo(() => {
    if (!displayGeoJSON) return [];
    const feats = displayGeoJSON.type === "FeatureCollection" ? (displayGeoJSON.features || [])
                 : displayGeoJSON.type === "Feature" ? [displayGeoJSON] : [];
    const arr = [];

    feats.forEach((f, i) => {
      try {
        if (viewMode === "comunas") {
          const name = f?.properties?.Comuna || f?.properties?.COMUNA || f?.properties?.NOM_COM || f?.properties?.name || `Zona ${i + 1}`;
          const count = conteoComuna[normKey(name)] ?? 0;
          const center = L.geoJSON(f).getBounds().getCenter();
          arr.push({ id: `c-${i}`, text: `${name} (${count})`, pos: [center.lat, center.lng] });
        } else {
          const macroName = getMacroName(f?.properties) || `Macrozona ${i + 1}`;
          const nk = normKey(macroName);
          if (!macrosConComunasActivas.has(nk)) {
            const center = L.geoJSON(f).getBounds().getCenter();
            arr.push({ id: `m-${i}`, text: macroName, pos: [center.lat, center.lng] });
          }
        }
      } catch {}
    });

    if (viewMode === "macrozona") {
      for (const [ckey, cinfo] of Object.entries(comunasIndex)) {
        const cnt = conteoComuna[ckey] || 0;
        if (cnt > 0) {
          arr.push({ id: `mc-${ckey}`, text: `${cinfo.name} (${cnt})`, pos: cinfo.center });
        }
      }
    }
    return arr;
  }, [displayGeoJSON, viewMode, conteoComuna, comunasIndex, macrosConComunasActivas]);

  return (
    <div className={`${styles.card} ${styles.mapCard}`} style={{ position: "relative" }}>
      {/* Header sticky */}
      <div
        className={styles.cardHeader}
        style={{
          gap: 12, alignItems: "center", flexWrap: "wrap",
          position: "sticky", top: 0, zIndex: 1000,
          background: "rgba(17,17,17,0.85)", backdropFilter: "blur(6px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <h3 className={styles.cardTitle} style={{ marginRight: "auto" }}>
          Mapa Colecta — Preparando / Guía
        </h3>

        {/* 🔘 Botones en fila (nowrap) */}
        <div style={{ display: "flex", gap: 8, flexWrap: "nowrap" }}>
          {/* Alternar comunas / macrozonas */}
          <button
            className={styles.tabBtn}
            onClick={() => { setViewMode(viewMode === "comunas" ? "macrozona" : "comunas"); shouldFitRef.current = true; }}
          >
            {viewMode === "comunas" ? "ORDENAR POR MACROZONAS" : "ORDENAR POR COMUNAS"}
          </button>

          {/* Filtros + cantidades */}
          <button
            className={styles.tabBtn}
            aria-pressed={filter === "todos"}
            onClick={() => setFilter("todos")}
            style={filter === "todos" ? SELECTED_BTN_STYLE : undefined}
          >
            TODOS ({btnCounts.todos})
          </button>
          <button
            className={styles.tabBtn}
            aria-pressed={filter === "preparando"}
            onClick={() => setFilter("preparando")}
            style={filter === "preparando" ? SELECTED_BTN_STYLE : undefined}
          >
            PREPARANDO ({btnCounts.preparando})
          </button>
          <button
            className={styles.tabBtn}
            aria-pressed={filter === "guia"}
            onClick={() => setFilter("guia")}
            style={filter === "guia" ? SELECTED_BTN_STYLE : undefined}
          >
            GUÍA ({btnCounts.guia})
          </button>
          <button
            className={styles.tabBtn}
            aria-pressed={filter === "novedad"}
            onClick={() => setFilter("novedad")}
            style={filter === "novedad" ? SELECTED_BTN_STYLE : undefined}
          >
            NOVEDAD ({btnCounts.novedad})
          </button>
          <button
            className={styles.tabBtn}
            aria-pressed={filter === "encamino"}
            onClick={() => setFilter("encamino")}
            style={filter === "encamino" ? SELECTED_BTN_STYLE : undefined}
          >
            EN CAMINO ({btnCounts.encamino})
          </button>
          {/* NUEVO: RECOLECTADO */}
          <button
            className={styles.tabBtn}
            aria-pressed={filter === "recolectado"}
            onClick={() => setFilter("recolectado")}
            style={filter === "recolectado" ? SELECTED_BTN_STYLE : undefined}
          >
            RECOLECTADO ({btnCounts.recolectado})
          </button>
        </div>
      </div>

      <div className={styles.mapCanvas} style={{ position: "relative" }}>
        {/* Leyenda por macrozona con totales y comunas (N) */}
        {viewMode === "macrozona" && (
          <div
            style={{
              position: "absolute", right: 12, top: 12, zIndex: 1000,
              background: "rgba(17,17,17,0.9)", color: "#fff",
              padding: "12px 14px", borderRadius: 12, fontSize: 12,
              border: "1px solid rgba(255,255,255,0.15)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              width: "auto",
              maxHeight: "60vh",
              overflowY: "auto",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              Macrozona
            </div>
            {Object.entries(macroLegend).map(([key, bucket]) => (
              <div key={key} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 14, height: 14, background: bucket.color, borderRadius: 3, display: "inline-block" }} />
                  <span>
                    {bucket.label}{macroTotals[key] ? ` (${macroTotals[key]})` : ""}
                  </span>
                </div>
                {bucket.items.length > 0 && (
                  <div style={{ marginLeft: 22, opacity: 0.95 }}>
                    {bucket.items.map((line) => (
                      <div key={line} style={{ whiteSpace: "nowrap" }}>• {line}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <MapContainer
          center={[-33.45, -70.66]}
          zoom={11}
          scrollWheelZoom
          whenCreated={(m) => (mapRef.current = m)}
          keyboard={false}
          style={{ width: "100%", height: "100%" }}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; OpenStreetMap contributors &copy; CARTO'
          />

          {displayGeoJSON && (
            <>
              <GeoJSON
                key={viewMode}
                data={displayGeoJSON}
                ref={geojsonRef}
                style={(feature) => {
                  if (viewMode === "macrozona") {
                    const name = getMacroName(feature?.properties) || "Rural";
                    const fill = macroColor(name);
                    return {
                      stroke: true, color: fill, weight: 1.2, opacity: 0.9,
                      fill: true, fillColor: fill, fillOpacity: 0.6,
                    };
                  }
                  const nombre =
                    feature?.properties?.Comuna ||
                    feature?.properties?.COMUNA ||
                    feature?.properties?.NOM_COM ||
                    feature?.properties?.name ||
                    "";
                  const count = conteoComuna[normKey(nombre)] ?? 0;
                  return {
                    stroke: true, color: "#9aa3b2", weight: 1, opacity: 0.6,
                    fill: true, fillColor: colorByCount(count), fillOpacity: 0.75,
                  };
                }}
                onEachFeature={(feature, layer) => {
                  const isMacro = viewMode === "macrozona";
                  const nombre = isMacro
                    ? (getMacroName(feature?.properties) || "Macrozona")
                    : (feature?.properties?.Comuna || feature?.properties?.COMUNA || feature?.properties?.NOM_COM || feature?.properties?.name || "Zona");
                  const count = !isMacro ? (conteoComuna[normKey(nombre)] ?? 0) : undefined;

                  layer.bindPopup(isMacro ? `${nombre}` : `${nombre} (${count}) pedidos`);

                  // UX: zoom al clic y highlight al hover
                  layer.on("click", () => {
                    const b = layer.getBounds?.();
                    if (b && b.isValid()) mapRef.current?.fitBounds(b, { padding: [20, 20] });
                  });
                  layer.on("mouseover", () => layer.setStyle({ weight: isMacro ? 2 : 2, opacity: 0.9 }));
                  layer.on("mouseout",  () => layer.setStyle({ weight: isMacro ? 1.2 : 1, opacity: 0.6 }));
                }}
              />

              {labels.map((lbl) => (
                <Marker key={lbl.id} position={lbl.pos} icon={labelIcon(lbl.text)} interactive={false} />
              ))}
            </>
          )}
        </MapContainer>
      </div>
    </div>
  );
}
